import type { DisputeTypeApiResponse, DisputeCategoryApiResponse, WorkflowApiResponse, WorkflowStageApiResponse } from "@/types/api/dispute-types";
import type { DisputeType, DisputeWorkflow, StageProperties, NotificationConfig } from "@/types/dispute-types";

function createDefaultWorkflow(disputeTypeId: string): DisputeWorkflow {
  return {
    id: `wf-${disputeTypeId}`,
    disputeTypeId,
    stages: [
      {
        id: `stage-${disputeTypeId}-1`,
        name: "Submitted",
        description: "Initial dispute submission",
        order: 1,
        color: "#3B82F6",
        icon: "FileText",
        properties: {
          requiresApproval: false,
          slaHours: 24,
          autoEscalate: false,
          allowReassignment: true,
        },
        notifications: [],
      },
      {
        id: `stage-${disputeTypeId}-2`,
        name: "Under Review",
        description: "Dispute is being reviewed",
        order: 2,
        color: "#8B5CF6",
        icon: "Search",
        properties: {
          requiresApproval: false,
          slaHours: 72,
          autoEscalate: true,
          allowReassignment: true,
        },
        notifications: [],
      },
      {
        id: `stage-${disputeTypeId}-3`,
        name: "Resolved",
        description: "Dispute has been resolved",
        order: 3,
        color: "#10B981",
        icon: "CheckCircle2",
        properties: {
          requiresApproval: false,
          autoEscalate: false,
          allowReassignment: false,
        },
        notifications: [],
      },
    ],
    transitions: [
      {
        id: `trans-${disputeTypeId}-1`,
        fromStageId: `stage-${disputeTypeId}-1`,
        toStageId: `stage-${disputeTypeId}-2`,
        name: "Begin Review",
        requiresComment: false,
        requiresApproval: false,
        autoTransition: false,
      },
      {
        id: `trans-${disputeTypeId}-2`,
        fromStageId: `stage-${disputeTypeId}-2`,
        toStageId: `stage-${disputeTypeId}-3`,
        name: "Resolve",
        requiresComment: true,
        requiresApproval: false,
        autoTransition: false,
      },
    ],
  };
}

function parseStageProperties(propertiesJson?: string): StageProperties {
  if (!propertiesJson) {
    return { requiresApproval: false, autoEscalate: false, allowReassignment: true };
  }
  try {
    return JSON.parse(propertiesJson) as StageProperties;
  } catch {
    return { requiresApproval: false, autoEscalate: false, allowReassignment: true };
  }
}

function parseNotifications(notificationsJson?: string): NotificationConfig[] {
  if (!notificationsJson) return [];
  try {
    return JSON.parse(notificationsJson) as NotificationConfig[];
  } catch {
    return [];
  }
}

export function mapWorkflowApiToUi(
  workflow: WorkflowApiResponse,
  stages: WorkflowStageApiResponse[]
): DisputeWorkflow {
  return {
    id: String(workflow.id),
    disputeTypeId: String(workflow.disputeTypeId),
    stages: stages
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(stage => ({
        id: String(stage.id),
        name: stage.name,
        description: stage.description ?? '',
        order: stage.sortOrder,
        color: stage.color ?? '#3B82F6',
        icon: stage.icon,
        properties: parseStageProperties(stage.properties),
        notifications: parseNotifications(stage.notifications),
        requiredFields: stage.requiredFields ? JSON.parse(stage.requiredFields) : undefined,
        allowedActions: stage.allowedActions ? JSON.parse(stage.allowedActions) : undefined,
      })),
    transitions: [],
  };
}

export function mapDisputeTypeApiToUi(api: DisputeTypeApiResponse, workflow?: DisputeWorkflow): DisputeType {
  const id = String(api.id);
  return {
    id,
    name: api.name,
    description: api.description ?? "",
    isActive: api.isActive,
    isSystemType: api.isSystemType ?? false,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
    createdBy: api.createdByUserName ?? "System",
    workflow: workflow ?? createDefaultWorkflow(id),
    usageCount: api.usageCount,
    avgResolutionTime: api.avgResolutionTime ?? 0,
    successRate: api.successRate ?? 0,
  };
}

export interface DisputeCategoryOption {
  id: number;
  code: string;
  name: string;
}

export function mapDisputeCategoryApiToOption(api: DisputeCategoryApiResponse): DisputeCategoryOption {
  return {
    id: api.id,
    code: api.code,
    name: api.name,
  };
}
